// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import * as api from '../../api/client'

// THE delivery-status chip for the provider portal.
//
// The portal answers one question for a prescriber — "did my patient get their medication?" —
// so DELIVERY is the status it shows, not the PMS status mirrored from PioneerRx. One component
// so the three provider surfaces (Rx detail, patient Rx table, My Prescriptions) render and
// behave identically: click opens the Proof-of-Delivery page, the same document the shipping
// drawer's "Download POD" opens.
//
// Clicking mints a short-lived signed link (api.openRxPodDoc) and opens the server-rendered
// /api/rx/{id}/pod-doc?tk=… page — the same BuildPodDocAsync output as the drawer's
// /api/shipping/shipments/{id}/pod-doc?tk=… — so the page's own print + Courier POD buttons work.

const TONES: Record<string, { bg: string; fg: string; label: string }> = {
  delivered:        { bg: '#dcfce7', fg: '#166534', label: '✓ Delivered' },
  out_for_delivery: { bg: '#dbeafe', fg: '#1e40af', label: '⛟ Out for delivery' },
  in_transit:       { bg: '#dbeafe', fg: '#1e40af', label: '⛟ In transit' },
  picked_up:        { bg: '#dbeafe', fg: '#1e40af', label: '⛟ Picked up' },
  label_purchased:  { bg: '#e0e7ff', fg: '#3730a3', label: '⛟ Label created' },
  created:          { bg: '#e0e7ff', fg: '#3730a3', label: '⛟ Order created' },
  exception:        { bg: '#fee2e2', fg: '#991b1b', label: '⚠ Delivery issue' },
  returned:         { bg: '#fee2e2', fg: '#991b1b', label: '⚠ Returned' },
}

export function ShipStatusPill({
  rxId, status, deliveredAt, size = 'md',
}: {
  rxId: string
  status?: string | null
  deliveredAt?: string | null
  size?: 'sm' | 'md'
}) {
  // No shipment on record → a plain dash. The provider is told nothing has shipped, not shown
  // a status borrowed from another system.
  if (!status || status === 'cancelled') return <span style={{ color: 'var(--color-text-mute)' }}>—</span>

  const t = TONES[status] ?? { bg: '#f3f4f6', fg: '#374151', label: status.replace(/_/g, ' ') }
  const dated = status === 'delivered' && deliveredAt
    ? `${t.label} ${new Date(deliveredAt).toLocaleDateString()}`
    : t.label

  const open = (e: React.MouseEvent) => {
    e.stopPropagation()   // rows are clickable; the chip must not also navigate to the Rx
    api.openRxPodDoc(rxId).catch(() => { alert('Could not open the delivery document. Please try again.') })
  }

  return (
    <span
      role="button"
      tabIndex={0}
      title="Open the proof-of-delivery document"
      onClick={open}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(e as unknown as React.MouseEvent) } }}
      style={{
        display: 'inline-block',
        fontSize: size === 'sm' ? 10.5 : 11,
        fontWeight: 600,
        padding: size === 'sm' ? '2px 7px' : '3px 9px',
        borderRadius: 999,
        background: t.bg,
        color: t.fg,
        cursor: 'pointer',
        textDecoration: 'underline dotted',
      }}
    >{dated} · POD ↗</span>
  )
}
